/**
 * Didit return-status → courtesy outcome view (PR-DIDIT-MOBILE-RETURN-2).
 *
 * When Didit finishes a verification it redirects to the workflow Success URL
 * (our `/registrazione-intermediario/verifica-identita`) appending a NON-secret
 * query string, e.g. `?status=Approved&verificationSessionId=<diditId>`.
 *
 * When the page has NO recoverable applicant session (operator/QR cross-device,
 * or a standalone re-verification link), we can still show a clean, status-aware
 * courtesy page using only that `status` param — never the uploadToken, never
 * any PII / Didit raw payload, no backend call.
 *
 * Pure mapping, unit-tested.
 */

export type DiditReturnKind = 'approved' | 'review' | 'failed' | 'retry';
export type DiditReturnTone = 'success' | 'info' | 'danger' | 'warning';

export interface DiditReturnOutcome {
  kind: DiditReturnKind;
  title: string;
  message: string;
  tone: DiditReturnTone;
  /** Show the primary "Torna al modulo" CTA (failed / invalid-link cases). */
  showReturnCta: boolean;
}

const OUTCOMES: Record<DiditReturnKind, DiditReturnOutcome> = {
  approved: {
    kind: 'approved',
    title: 'Verifica completata!',
    message: 'Tutto qui, non è richiesta nessuna ulteriore azione.',
    tone: 'success',
    showReturnCta: false,
  },
  review: {
    kind: 'review',
    title: 'Verifica in corso',
    message:
      'La tua richiesta è in fase di verifica, riceverai un aggiornamento al più presto.',
    tone: 'info',
    showReturnCta: false,
  },
  failed: {
    kind: 'failed',
    title: 'Verifica non riuscita',
    message:
      'La verifica non è andata a buon fine. Ti invitiamo a fare una nuova richiesta.',
    tone: 'danger',
    showReturnCta: true,
  },
  retry: {
    kind: 'retry',
    title: 'Link non valido',
    message:
      'Il link di verifica che hai fornito sembra non essere corretto. Ricomincia e scansiona di nuovo il codice QR.',
    tone: 'warning',
    showReturnCta: true,
  },
};

/**
 * Map a raw Didit `status` string to a courtesy outcome. Conservative: anything
 * not clearly approved / review / declined (incl. null, abandoned, expired,
 * not-started, unknown) falls back to the "retry" copy — it NEVER claims success.
 */
export function diditReturnOutcome(rawStatus: string | null | undefined): DiditReturnOutcome {
  const s = (rawStatus ?? '').trim().toLowerCase();
  if (s.length === 0) return OUTCOMES.retry;
  if (s.includes('approv')) return OUTCOMES.approved;
  if (s.includes('declin') || s.includes('reject') || s.includes('fail')) {
    return OUTCOMES.failed;
  }
  if (s.includes('review') || s.includes('progress') || s.includes('pending')) {
    return OUTCOMES.review;
  }
  // abandoned / expired / not started / unknown → ricomincia.
  return OUTCOMES.retry;
}

/**
 * Read the Didit `status` query param from a location search string. Tolerates a
 * leading `?` and returns null when absent / malformed.
 */
export function readDiditReturnStatus(search: string): string | null {
  if (!search) return null;
  try {
    const params = new URLSearchParams(search.replace(/^\?/, ''));
    const status = params.get('status');
    return status && status.length > 0 ? status : null;
  } catch {
    return null;
  }
}
